import * as React from 'preact/compat'
import Modal from '../Modal'
import buttonsCss from '../../../css/buttons.pcss.json'
import Api from '../../api'
import { captureException } from '../../common/helpers'
import { showToast } from '../toast'
import GameTagsSection from './GameTagsSection'
import cn from "classnames";
import SVG from "../SVG";
import SvelteWrapper from "../SvelteWrapper";
import IncludeExcludeFilter from "../IncludeExcludeFilter.svelte";
import commonCss from '../../../css/v9/common.pcss.json'

const IncludeExcludeFilter_React = SvelteWrapper(IncludeExcludeFilter)

export interface Tag {
  id: number,
  slug: string,
  name_singular: string,
  name_plural: string,
  section: string,
  hide_from_site: boolean,
  exclude?: boolean
}

interface PropTypes {
  tags: string[],
  name: string,
  innerHtml?: string,
  buttonClass?: string,
  hideTagsList?: boolean,
  numberInButton?: boolean,
  showHidden?: boolean,
  allowExclusion?: boolean,
  includeGameTypes?: boolean
}

interface StateTypes {
  tags: Tag[],
  allTags: Tag[],
  isModalOpen: boolean
}

// noinspection JSUnusedGlobalSymbols
export default class GameTagsEditor extends React.Component<PropTypes, StateTypes> {

  state: StateTypes = {
    tags: [],
    allTags: [],
    isModalOpen: false
  }

  getTagsFromProps(props: PropTypes) {
    return props.tags.map(tagSlug => {
      let exclude = false
      if (tagSlug.startsWith('-')) {
        exclude = true
        tagSlug = tagSlug.substring(1)
      }
      const tag = this.state.allTags.find(p => p.slug === tagSlug)
      return {
        ...tag,
        exclude
      }
    }).filter(tag => !!tag)
  }

  componentDidUpdate(previousProps: Readonly<PropTypes>) {
    if (previousProps.tags !== this.props.tags) {
      this.setState({
        tags: this.getTagsFromProps(this.props)
      })
    }
  }

  componentDidMount() {
    Api.get<{
      tags: Array<Tag>,
    }>({ url: 'games/tags' })
      .then(response => {
        let tags = response.tags
        if (!this.props.showHidden) {
          tags = tags.filter(tag => !tag.hide_from_site)
        }
        if (!this.props.includeGameTypes) {
          tags = tags.filter(tag => tag.section !== 'Тип')
        }
        this.setState({
          allTags: tags,
        }, () => {
          this.setState({
            tags: this.getTagsFromProps(this.props)
          })
        })
        window.addEventListener('reset-tags', () => {
          this.setState({
            tags: []
          })
        })
      })
      .catch(error => {
        captureException('Game Tags Editor', error)
        showToast({
          type: 'error',
          text: 'Не удалось загрузить список тегов. Попробуй позже.',
        })
      })
  }

  openModal = () => {
    this.setState({
      isModalOpen: true
    })
  }

  closeModal = () => {
    this.setState({
      isModalOpen: false
    })
  }

  handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.currentTarget
    const { tags } = this.state
    if (checked) {
      const tag = this.state.allTags.find(tag => tag.slug === value)
      this.setState({
        tags: [...tags, tag]
      })
    } else {
      this.setState({
        tags: tags.filter(tag => tag.slug !== value)
      })
    }
  }

  handleTagIncludeExclude = (tagSlug: string, add: boolean, exclude: boolean = false) => {
    const { tags, allTags } = this.state

    if (add) {
      const tag = {
        ...allTags.find(tag => tag.slug === tagSlug),
        exclude
      }
      this.setState({
        tags: [...tags.filter(tag => tag.slug !== tagSlug), tag]
      })
    } else {
      this.setState({
        tags: tags.filter(tag => tag.slug !== tagSlug)
      })
    }
  }

  renderGameTagsModal = () => {
    const { allTags } = this.state
    const allSections = []
    const sections = []
    allTags.forEach(tag => {
      if (!allSections.includes(tag.section)) {
        allSections.push(tag.section)
      }
    })
    const wideSections = ['Жанр', 'Тема']
    allSections.forEach((section) => {
      const tags = allTags.filter(tag => tag.section === section).map(tag => {
        const checked = this.state.tags.find(p => p.slug === tag.slug)

        if (this.props.allowExclusion) {
          return <IncludeExcludeFilter_React
            label={tag.name_singular}
            value={tag.slug}
            status={checked ? (checked.exclude ? -1 : 1) : 0}
            onChange={this.handleTagIncludeExclude}
          />
        }
        return <styled-checkbox
          name='tag[]'
          label={tag.name_singular}
          value={tag.slug}
          checked={!!checked}
          onChange={this.handleTagChange}
        />
      })
      sections.push(<GameTagsSection
        title={section}
        tags={tags}
        wide={wideSections.includes(section)}
      />)
    })

    return <Modal showClose={true} onClose={this.closeModal} enableScroll={true}>
      <h1 style='display: flex; column-gap: 8px; font-family: Rubik; font-weight: 500; font-size: 22px; margin-bottom: 16px'>
        Выбор тегов
        <a
          href="/games_guidelines"
          target="_blank"
          className={commonCss['link']}
          style='--link-color: var(--v9-color-text-min-vis)'
        >
          <SVG i="sg-new/question" adaptive/>
        </a>
      </h1>
      <div className={commonCss['game-tags-editor__grid']}>
        {sections}
      </div>
    </Modal>
  }

  removeTag = (tagSlug: string) => {
    const tags = [...this.state.tags].filter(tag => tag.slug !== tagSlug)
    this.setState({
      tags
    })
  }

  render() {
    const { name, numberInButton, innerHtml, hideTagsList } = this.props
    const { tags, isModalOpen, allTags } = this.state

    const tagsInputs = tags.map(tag => {
      let value = tag.slug
      if (tag.exclude) {
        value = `-${value}`
      }
      return <input
        type='hidden'
        name={`${name}[]`}
        value={value}
      />
    })

    const tagsString = tags.map(tag1 => {
      const tag = allTags.find(tag2 => tag2.slug === tag1.slug)
      if (!tag) {
        return '';
      }
      return <span style='display: flex; align-items: center'>
        {tag.name_singular}
        <button
          type='button'
          className={cn(buttonsCss['icon'], buttonsCss['icon--low-contrast'])}
          style='--icon-size: 32px'
          onClick={() => this.removeTag(tag.slug)}
        >
          <SVG i='sg-new/cross' adaptive/>
        </button>
      </span>
    })

    let buttonContent: string | React.JSX.Element = 'Выбрать теги'
    if (numberInButton && tags.length > 0) {
      const excludedTagsCount = tags.filter(p => p.exclude).length;
      const includedTagsCount = tags.filter(p => !p.exclude).length
      buttonContent = <>
        Теги&nbsp;
        {includedTagsCount > 0 && <span style='color: var(--green)'>+{includedTagsCount}</span>}
        {includedTagsCount > 0 && excludedTagsCount > 0 &&
          <span style='color: var(--v9-color-text-min-vis)'>/</span>}
        {excludedTagsCount > 0 && <span style='color: var(--brand-red)'>-{excludedTagsCount}</span>}
      </>
    } else if (innerHtml) {
      buttonContent = innerHtml
    }

    let buttonClass = buttonsCss['low-contrast']
    if (this.props.buttonClass) {
      buttonClass = this.props.buttonClass
    }

    return <>
      {tagsInputs}
      {!hideTagsList
        && <div style='display: flex; column-gap: 24px; row-gap: 8px; margin-bottom: 16px; flex-wrap: wrap'>
          {tagsString}
        </div>}
      <button className={buttonClass} type='button' onClick={this.openModal}>
        {buttonContent}
      </button>
      {isModalOpen && this.renderGameTagsModal()}
    </>
  }
}
